import { Badge, Button, Popover, Stack, Text } from '@mantine/core';
import { IconPlayerPlayFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Assignment } from '@/api/schedule';
import { useCreateEntry } from '@/api/timeEntries';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Geplante Arbeit eines Tages mit „Timer starten“ (CAL-06, SCH-06). */
export function PlannedBadge({ assignment, hours }: { assignment: Assignment; hours: number }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const create = useCreateEntry(ctx.workspaceId);
  const onError = useApiError();
  const [opened, setOpened] = useState(false);
  return (
    <Popover opened={opened} onChange={setOpened} withArrow position="bottom">
      <Popover.Target>
        <Badge
          size="xs"
          fullWidth
          mt={2}
          variant="outline"
          style={{ textTransform: 'none', borderColor: assignment.projectColor, color: assignment.projectColor, cursor: 'pointer', borderStyle: 'dashed' }}
          onClick={() => setOpened((o) => !o)}
        >
          {assignment.projectName} · {hours.toLocaleString(ctx.prefs.locale)} h
        </Badge>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6} maw={240}>
          <Text size="sm" fw={600}>
            {assignment.projectName}
            {assignment.taskName ? ` · ${assignment.taskName}` : ''}
          </Text>
          <Text size="xs" c="dimmed">
            {t('schedule.plannedHours', { hours: hours.toLocaleString(ctx.prefs.locale) })}
          </Text>
          {assignment.note && <Text size="xs">{assignment.note}</Text>}
          <Button
            size="xs"
            leftSection={<IconPlayerPlayFilled size={12} />}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { projectId: assignment.projectId, taskId: assignment.taskId ?? undefined, description: assignment.note ?? undefined },
                { onSuccess: () => setOpened(false), onError },
              )
            }
          >
            {t('schedule.startTimer')}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
